<script setup lang="ts">

import { ref } from "vue"
import { useGetDocs } from "@/mixins/modularFirebaseOperations"
import { quantityTypeOptions, currencyOptions, conditionOptions, requiresWholeUnits } from "@/composables/inventoryHelpers"
import type { AcInventoryItem, AcInventoryBatch, AcProductBrand, AcSupplier, AcProductCategory, AcInventoryLocation } from "@/types/inventory"

const emit = defineEmits(["item-added", "cancel"])

// Meta lists for the dropdowns
const { docs: brands, isLoadingDocs: isLoadingBrands } = useGetDocs<AcProductBrand>("inventory/brands/list")
const { docs: suppliers, isLoadingDocs: isLoadingSuppliers } = useGetDocs<AcSupplier>("inventory/suppliers/list")
const { docs: categories, isLoadingDocs: isLoadingCategories } = useGetDocs<AcProductCategory>("inventory/categories/list")
const { docs: locations, isLoadingDocs: isLoadingLocations } = useGetDocs<AcInventoryLocation>("inventory/locations/list")

const productName = ref("")
const productBrand = ref<AcProductBrand | null>(null)
const supplier = ref<AcSupplier | null>(null)
const productCategory = ref<AcProductCategory | null>(null)
const productSKU = ref("")
const quantity = ref<number | null>(null)
const quantityType = ref<AcInventoryItem["quantityType"]>("PIECE")
const unitPrice = ref<number | null>(null)
const purchaseCurrency = ref<NonNullable<AcInventoryBatch["purchaseCurrency"]>>("TRY")
const condition = ref<NonNullable<AcInventoryBatch["condition"]>>("NEW")
const location = ref<AcInventoryLocation | null>(null)
const productDescription = ref("")

const saveItem = () => {
    if (!productName.value.trim()) {
        alert("Lütfen ürün adını giriniz.")
        return
    }
    if (!productCategory.value) {
        alert("Lütfen bir kategori seçiniz.")
        return
    }
    if (quantity.value === null || isNaN(Number(quantity.value)) || Number(quantity.value) <= 0) {
        alert("Lütfen geçerli bir miktar giriniz.")
        return
    }
    if (requiresWholeUnits(quantityType.value) && !Number.isInteger(Number(quantity.value))) {
        alert("Adet ile takip edilen ürünler için lütfen tam sayı giriniz.")
        return
    }
    if (unitPrice.value === null || isNaN(Number(unitPrice.value)) || Number(unitPrice.value) < 0) {
        alert("Lütfen geçerli bir birim alış fiyatı giriniz.")
        return
    }

    // The first batch is built by the list handler from these fields.
    const newItem: Partial<AcInventoryItem> = {
        productName: productName.value.trim(),
        productCategory: productCategory.value,
        quantity: Number(quantity.value),
        quantityType: quantityType.value,
        originalPrice: Number(unitPrice.value),
        purchaseCurrency: purchaseCurrency.value,
        condition: condition.value,
    }

    // Only include optional fields when provided
    if (productBrand.value) newItem.productBrand = productBrand.value
    if (supplier.value) newItem.supplier = supplier.value
    if (productSKU.value.trim()) newItem.productSKU = productSKU.value.trim()
    if (location.value) newItem.location = location.value
    if (productDescription.value.trim()) newItem.productDescription = productDescription.value.trim()

    emit("item-added", newItem)
}

</script>

<template>
    <form @submit.prevent="saveItem" class="ac-main-form !border-none">

        <div class="ac-form-control w-full">
            <label for="productName">Ürün Adı</label>
            <input id="productName" type="text" v-model="productName" required>
        </div>

        <div class="flex gap-4">
            <div class="ac-form-control w-full">
                <label for="productBrand">Marka</label>
                <select id="productBrand" v-model="productBrand" class="!py-3">
                    <option :value="null">{{ isLoadingBrands ? 'Yükleniyor...' : 'Marka seçiniz' }}</option>
                    <option v-for="brand in brands" :key="brand.id" :value="brand">{{ brand.name }}</option>
                </select>
                <span class="optional-label">opsiyonel</span>
            </div>

            <div class="ac-form-control w-full">
                <label for="supplier">Tedarikçi</label>
                <select id="supplier" v-model="supplier" class="!py-3">
                    <option :value="null">{{ isLoadingSuppliers ? 'Yükleniyor...' : 'Tedarikçi seçiniz' }}</option>
                    <option v-for="sup in suppliers" :key="sup.id" :value="sup">{{ sup.name }}</option>
                </select>
                <span class="optional-label">opsiyonel</span>
            </div>
        </div>

        <div class="flex gap-4">
            <div class="ac-form-control w-full">
                <label for="productCategory">Kategori</label>
                <select id="productCategory" v-model="productCategory" required class="!py-3">
                    <option :value="null" disabled>{{ isLoadingCategories ? 'Yükleniyor...' : 'Kategori seçiniz' }}</option>
                    <option v-for="cat in categories" :key="cat.id" :value="cat">{{ cat.name }}</option>
                </select>
            </div>

            <div class="ac-form-control w-full">
                <label for="productSKU">SKU / Stok Kodu</label>
                <input id="productSKU" type="text" v-model="productSKU">
                <span class="optional-label">opsiyonel</span>
            </div>
        </div>

        <div class="flex gap-4">
            <div class="ac-form-control w-full">
                <label for="quantity">Miktar</label>
                <input id="quantity" type="number" min="0" :step="requiresWholeUnits(quantityType) ? '1' : 'any'" v-model="quantity" required>
            </div>

            <div class="ac-form-control w-full">
                <label for="quantityType">Miktar Birimi</label>
                <select id="quantityType" v-model="quantityType" required class="!py-3">
                    <option v-for="opt in quantityTypeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
            </div>
        </div>

        <!-- First purchase batch: unit price, currency, condition -->
        <div class="flex gap-4">
            <div class="ac-form-control w-full">
                <label for="unitPrice">Birim Alış Fiyatı</label>
                <input id="unitPrice" type="number" min="0" step="any" v-model="unitPrice" required>
            </div>

            <div class="ac-form-control w-full">
                <label for="purchaseCurrency">Para Birimi</label>
                <select id="purchaseCurrency" v-model="purchaseCurrency" required class="!py-3">
                    <option v-for="opt in currencyOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
            </div>

            <div class="ac-form-control w-full">
                <label for="condition">Durum</label>
                <select id="condition" v-model="condition" required class="!py-3">
                    <option v-for="opt in conditionOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
            </div>
        </div>

        <div class="ac-form-control w-full">
            <label for="location">Depo / Konum</label>
            <select id="location" v-model="location" class="!py-3">
                <option :value="null">{{ isLoadingLocations ? 'Yükleniyor...' : 'Depo seçiniz' }}</option>
                <option v-for="loc in locations" :key="loc.id" :value="loc">{{ loc.name }}</option>
            </select>
            <span class="optional-label">opsiyonel</span>
        </div>

        <div class="ac-form-control w-full">
            <label for="productDescription">Ürün Açıklaması</label>
            <textarea id="productDescription" rows="3" v-model="productDescription"></textarea>
            <span class="optional-label">opsiyonel</span>
        </div>

        <div class="flex items-center justify-center gap-6 w-full">
            <button type="submit" class="ac-primary-button text-2xl mt-4">Ürün Oluştur</button>
            <button type="button" @click="$emit('cancel')" class="ac-secondary-button text-2xl mt-4">İptal</button>
        </div>
    </form>
</template>

<style scoped>
.optional-label {
    font-size: 1.1rem;
    color: #9ca3af;
    margin-top: 0.25rem;
    font-style: italic;
}
</style>
